import React, { useMemo, useState } from "react";
import Model, { Providers } from "@tripian/model";
import moment from "moment";
import isoLanguages from "./isoLanguages";
import { Button, RSelect, SvgIcons, TextField } from "@tripian/react";
import ParticipantQuestions from "./Questions/ParticipantQuestions/ParticipantQuestions";
import BookingQuestions from "./Questions/ParticipantQuestions/BookingQuestions";
import classes from "./GygTourOption.module.scss";

interface IGygTourOption {
  tourPriceBreakdownOption?: Providers.Gyg.TourPriceBreakdownOption;
  tourOption?: Providers.Gyg.TourOptionNew;
  title?: string;
  startTime?: string;
  personsCategories: (Providers.Gyg.PersonCategory & { count: number })[];
  participantsRange?: {
    min: number;
    max: number;
  };
  bookingRequestCallback: (
    bookingRequest: Providers.Gyg.TourBookingRequest
  ) => void;
  t: (value: Model.TranslationKey) => string;
  isSelected: boolean;
  setSelectedOption: (id: number) => void;
  selectedDateTime: string;
  setSelectedDateTime: (dateTime: string) => void;
  loading?: boolean;
}

const GygTourOption: React.FC<IGygTourOption> = ({
  tourPriceBreakdownOption,
  tourOption,
  title,
  startTime,
  personsCategories,
  participantsRange,
  bookingRequestCallback,
  t,
  isSelected,
  setSelectedOption,
  selectedDateTime,
  setSelectedDateTime,
  loading = false,
}) => {
  moment.locale(window.twindow.langCode);

  //const [selectedDateTime, setSelectedDateTime] = useState<string>(tourPriceBreakdownOption?.time_slots[0]?.date_time || "");
  const [bookingAnswers, setBookingAnswers] = useState<{ [key: string]: any }>(
    {}
  );
  const [participantAnswers, setParticipantAnswers] = useState<{
    [key: string]: any;
  }>({});
  const [freeTextQuestion, setFreeTextQuestion] = useState<
    string | undefined
  >();
  const [conductionLanguage, setConductionLanguage] = useState<
    { type: string; value: string } | undefined
  >();
  const [showAllTimes, setShowAllTimes] = useState(false);

  const selectedTimeSlot: Providers.Gyg.TourPriceBreakdownTimeSlot | undefined =
    useMemo(
      () =>
        tourPriceBreakdownOption?.time_slots.find(t =>
          moment(t.date_time)
            .utcOffset(0)
            .isSame(moment(selectedDateTime).utcOffset(0))
        ),
      [selectedDateTime, tourPriceBreakdownOption?.time_slots]
    );

  const totalCountAndInRange = useMemo((): boolean => {
    const totalCount = personsCategories.reduce(
      (acc, category) => acc + category.count,
      0
    );
    if (!participantsRange) return true;
    return (
      totalCount >= participantsRange.min && totalCount <= participantsRange.max
    );
  }, [participantsRange, personsCategories]);

  // Saatler için See more/less mantığı
  const timeSlots = useMemo(
    () => tourPriceBreakdownOption?.time_slots || [],
    [tourPriceBreakdownOption?.time_slots]
  );
  const visibleSlots = showAllTimes ? timeSlots : timeSlots.slice(0, 7);

  const getCategories = selectedTimeSlot => {
    const mergedCategories = {};

    if (!selectedTimeSlot?.price_breakdown) {
      return []; // Return an empty array if price_breakdown is undefined
    }

    // Use personsCategories instead of selectedTimeSlot.price_breakdown
    personsCategories.forEach(personCategory => {
      if (personCategory.count > 0) {
        // Find matching category from price breakdown
        const matchingIndividual =
          selectedTimeSlot.price_breakdown.individuals?.find(
            individual =>
              individual.ticket_category === personCategory.ticket_category
          );
        const matchingGroup = selectedTimeSlot.price_breakdown.groups?.find(
          group => group.ticket_category === personCategory.ticket_category
        );

        const categoryId =
          matchingIndividual?.category_id || matchingGroup?.category_id;
        const ticketCategory = personCategory.ticket_category;

        if (categoryId) {
          if (mergedCategories[categoryId]) {
            mergedCategories[categoryId].number_of_participants +=
              personCategory.count;
          } else {
            mergedCategories[categoryId] = {
              number_of_participants: personCategory.count,
              ticketCategory,
            };
          }
        }
      }
    });

    return Object.keys(mergedCategories).map(categoryId => ({
      category_id: Number(categoryId),
      number_of_participants:
        mergedCategories[Number(categoryId)].number_of_participants,
      ticketCategory: mergedCategories[Number(categoryId)].ticketCategory,
    }));
  };

  const renderParameters = useMemo(() => {
    if (!tourOption) return undefined;

    const languageOptions = (type: string, languages: string[]) =>
      languages.map(lang => ({
        type,
        value: lang,
        label: `${isoLanguages.find(iso => iso.code === lang)?.name || ""} (${type.replace("language_", "").toUpperCase()})`,
      }));

    const allLanguages = [
      ...languageOptions(
        "language_audio",
        tourOption.cond_language?.language_audio || []
      ),
      ...languageOptions(
        "language_booklet",
        tourOption.cond_language?.language_booklet || []
      ),
      ...languageOptions(
        "language_live",
        tourOption.cond_language?.language_live || []
      ),
    ];

    if (allLanguages.length > 0 && !conductionLanguage) {
      setConductionLanguage({
        type: allLanguages[0].type,
        value: allLanguages[0].value,
      });
    }

    return (
      <div>
        {/* Booking Questions */}
        {tourOption.questions.booking_questions && (
          <BookingQuestions
            questions={tourOption.questions.booking_questions}
            onAnswers={answers => setBookingAnswers(answers)}
            t={t}
          />
        )}

        {/* Participant Questions */}
        {tourOption.questions.participant_questions.length > 0 &&
          personsCategories.map(category =>
            Array.from({ length: category.count }).map((_, travelerIndex) => (
              <div
                key={`traveler_${category.ticket_category}_${travelerIndex}`}
              >
                <ParticipantQuestions
                  questions={tourOption.questions.participant_questions}
                  onAnswers={answers =>
                    setParticipantAnswers(prev => ({
                      ...prev,
                      [category.ticket_category]: [
                        ...(prev[category.ticket_category] || []),
                        { participant_answers: answers },
                      ],
                    }))
                  }
                  t={t}
                />
              </div>
            ))
          )}

        {/* Free Text Question */}
        {Object.keys(tourOption.questions.free_text_question).length > 0 && (
          <div>
            <h4 className={classes.parameters}>
              {tourOption.questions.free_text_question.description}{" "}
              {tourOption.questions.free_text_question.mandatory ? "(*)" : ""}
            </h4>
            <TextField
              type="text"
              name="free_text_question"
              value={freeTextQuestion || ""}
              onChange={event => setFreeTextQuestion(event.target.value)}
            />
          </div>
        )}
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourOption, conductionLanguage, personsCategories, freeTextQuestion]);

  const addToCart = () => {
    const optionId = tourOption?.option_id;

    if (selectedTimeSlot) {
      const retailPrice =
        selectedTimeSlot.price_breakdown.price_summary.retail_price;

      if (retailPrice !== undefined) {
        const categories = getCategories(selectedTimeSlot);

        const formattedParticipantQuestions = categories.map(category => {
          const personCategory = personsCategories.find(
            pCategory => pCategory.ticket_category === category.ticketCategory
          );

          return {
            category_id: category.category_id,
            participant_answers: participantAnswers[
              personCategory?.ticket_category || category.category_id
            ]?.reduce(
              (acc, answers) => ({
                ...acc,
                ...answers,
              }),
              {}
            ),
          };
        });

        const bookingRequest: Providers.Gyg.TourBookingRequest = {
          base_data: {
            cnt_language: "en",
            currency: "USD",
          },
          data: {
            booking: {
              bookable: {
                external_reference_id: "tripian",
                option_id: optionId || 0,
                datetime: selectedDateTime,
                price: retailPrice,
                categories: categories.map(category => ({
                  category_id: category.category_id,
                  number_of_participants: category.number_of_participants,
                })),
                questions: {
                  booking_questions:
                    Object.keys(bookingAnswers).length > 0
                      ? bookingAnswers
                      : undefined,
                  participant_questions:
                    formattedParticipantQuestions.length > 0
                      ? formattedParticipantQuestions
                      : undefined,
                  conduction_language: conductionLanguage,
                  free_text_question: freeTextQuestion,
                },
              },
            },
          },
        };

        bookingRequestCallback(bookingRequest);
      }
    } else {
      console.error("Option ID, retail price, or questions are undefined.");
    }
  };

  return (
    <div
      className={`${classes.mainDiv} border-2 border-solid border-slate-200`}
    >
      {totalCountAndInRange ? (
        <div
          className="w-[calc(100%-0.25rem)]"
          onClick={() => setSelectedOption(tourOption?.option_id!)}
          style={{ cursor: "pointer" }}
        >
          <div className="p-4">
            <div className="flex gap-4 items-start">
              <div className="mt-[0.125rem]">
                {isSelected ? (
                  <SvgIcons.RadioButtonChecked />
                ) : (
                  <SvgIcons.RadioButtonUnChecked />
                )}
              </div>
              <div>
                <div className="font-bold text-lg mb-2">{title}</div>

                <div className={classes.gygTourOptionHour}>
                  <SvgIcons.Clock /> {tourOption?.duration}{" "}
                  {tourOption?.duration_unit}
                </div>
                {tourOption?.meeting_point && (
                  <div className="mb-2">
                    {t(
                      "trips.myTrips.localExperiences.tourDetails.meetingPoint"
                    )}
                    : {tourOption?.meeting_point}
                  </div>
                )}
                {tourOption?.pick_up && (
                  <div className="mb-2">
                    {t("trips.myTrips.localExperiences.tourDetails.pickup")}:{" "}
                    {tourOption?.pick_up}
                  </div>
                )}

                {/* Conduction Language */}
                {tourOption?.questions?.conduction_language?.mandatory && (
                  <div className="my-8">
                    <div className="font-medium text-sm text-gray-600 mb-1">
                      {t(
                        "trips.myTrips.localExperiences.tourDetails.conductionLanguage"
                      )}{" "}
                      (*)
                    </div>
                    <RSelect
                      options={(() => {
                        const languageOptions = (
                          type: string,
                          languages: string[]
                        ) =>
                          languages.map(lang => ({
                            type,
                            value: lang,
                            label: `${isoLanguages.find(iso => iso.code === lang)?.name || ""} (${type.replace("language_", "").toUpperCase()})`,
                          }));
                        const allLanguages = [
                          ...languageOptions(
                            "language_audio",
                            tourOption.cond_language?.language_audio || []
                          ),
                          ...languageOptions(
                            "language_booklet",
                            tourOption.cond_language?.language_booklet || []
                          ),
                          ...languageOptions(
                            "language_live",
                            tourOption.cond_language?.language_live || []
                          ),
                        ];
                        if (allLanguages.length > 0 && !conductionLanguage) {
                          setConductionLanguage({
                            type: allLanguages[0].type,
                            value: allLanguages[0].value,
                          });
                        }
                        return allLanguages.map(lg => ({
                          value: lg.value,
                          label: lg.label,
                        }));
                      })()}
                      selectedOptionValue={conductionLanguage?.value}
                      onSelectedOptionChange={selectedOption => {
                        const allLanguages = (() => {
                          const languageOptions = (
                            type: string,
                            languages: string[]
                          ) =>
                            languages.map(lang => ({
                              type,
                              value: lang,
                              label: `${isoLanguages.find(iso => iso.code === lang)?.name || ""} (${type.replace("language_", "").toUpperCase()})`,
                            }));
                          return [
                            ...languageOptions(
                              "language_audio",
                              tourOption.cond_language?.language_audio || []
                            ),
                            ...languageOptions(
                              "language_booklet",
                              tourOption.cond_language?.language_booklet || []
                            ),
                            ...languageOptions(
                              "language_live",
                              tourOption.cond_language?.language_live || []
                            ),
                          ];
                        })();
                        const selectedType =
                          allLanguages.find(
                            lg => lg.value === selectedOption.value
                          )?.type || "";
                        setConductionLanguage({
                          type: selectedType,
                          value: selectedOption.value,
                        });
                      }}
                    />
                  </div>
                )}
                <div className={classes.startTime}>
                  {moment(startTime).format("dddd, ll")}
                </div>
                {isSelected && (
                  <>
                    <div className={classes.timeOptions}>
                      {visibleSlots.map((time_slot, index) => (
                        <div
                          key={index}
                          className={`${classes.timeOption} ${selectedDateTime === time_slot.date_time ? classes.selectedTimeOption : ""}`}
                          style={selectedDateTime !== time_slot.date_time ? { backgroundColor: "#fff" } : undefined}
                          onClick={e => {
                            e.stopPropagation();
                            setSelectedDateTime(time_slot.date_time);
                          }}
                        >
                          {moment(time_slot.date_time).format("LT")}
                        </div>
                      ))}
                      {timeSlots.length > 7 && (
                        <div className="flex w-full items-center justify-center my-2 font-bold text-blue-950">
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              setShowAllTimes(prev => !prev);
                            }}
                            className={classes.seeMoreButton}
                          >
                            {showAllTimes
                              ? t(
                                  "trips.myTrips.localExperiences.tourDetails.seeLessTimes"
                                )
                              : t(
                                  "trips.myTrips.localExperiences.tourDetails.seeMoreTimes"
                                )}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="hide-s text-right pr-2">
                      <div>
                        {selectedTimeSlot?.price_breakdown?.individuals &&
                          selectedTimeSlot.price_breakdown.individuals.map(
                            (i, index) => (
                              <div key={`${i.ticket_category}-${index}`}>
                                <span style={{ textTransform: "capitalize" }}>
                                  {i.ticket_category}
                                </span>
                                <span>{i.quantity}</span> ×{" "}
                                <span>{i.price.customer_base_price}</span>
                                <span>{i.price.currency}</span>
                              </div>
                            )
                          )}
                        {selectedTimeSlot?.price_breakdown?.groups &&
                          selectedTimeSlot.price_breakdown.groups.map(
                            (g, index) => (
                              <div key={`group-${g.category_id}-${index}`}>
                                <span style={{ textTransform: "capitalize" }}>
                                  {t(
                                    "trips.myTrips.localExperiences.tourDetails.group"
                                  )}
                                  ,{" "}
                                </span>
                                <span>
                                  {g.group_breakdown.quantity}{" "}
                                  {t(
                                    "trips.myTrips.localExperiences.tourDetails.person"
                                  )}
                                </span>{" "}
                                :{" "}
                                <span>
                                  {g.group_breakdown.price.customer_base_price}
                                </span>
                                <span>{g.group_breakdown.price.currency}</span>
                              </div>
                            )
                          )}
                      </div>
                      <div>
                        <span className={classes.gygTourOptionsBottomPrice}>
                          {
                            selectedTimeSlot?.price_breakdown?.price_summary
                              ?.retail_price
                          }
                        </span>
                        &nbsp;
                        <span className={classes.gygTourOptionsBottomPrice}>
                          {
                            selectedTimeSlot?.price_breakdown?.price_summary
                              ?.currency
                          }
                        </span>
                      </div>
                      <Button
                        className={classes.addToCartButton}
                        disabled={loading}
                        onClick={addToCart}
                        text={loading ? "..." : t(
                          "trips.myTrips.localExperiences.tourDetails.bookNow"
                        )}
                      />
                    </div>
                    <div className="hide-m">
                      <hr className="w-full h-px my-4 bg-gray-200 border-0" />
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <span className={classes.gygTourOptionsBottomPrice}>
                            {
                              selectedTimeSlot?.price_breakdown?.price_summary
                                ?.retail_price
                            }
                          </span>
                          &nbsp;
                          <span className={classes.gygTourOptionsBottomPrice}>
                            {
                              selectedTimeSlot?.price_breakdown?.price_summary
                                ?.currency
                            }
                          </span>
                        </div>
                        <div>
                          {selectedTimeSlot?.price_breakdown?.individuals &&
                            selectedTimeSlot.price_breakdown.individuals.map(
                              (i, index) => (
                                <div key={`${i.ticket_category}-${index}`}>
                                  <span style={{ textTransform: "capitalize" }}>
                                    {i.ticket_category}
                                  </span>
                                  <span>{i.quantity}</span> ×{" "}
                                  <span>{i.price.customer_base_price}</span>
                                  <span>{i.price.currency}</span>
                                </div>
                              )
                            )}
                          {selectedTimeSlot?.price_breakdown?.groups &&
                            selectedTimeSlot.price_breakdown.groups.map(
                              (g, index) => (
                                <div key={`group-${g.category_id}-${index}`}>
                                  <span style={{ textTransform: "capitalize" }}>
                                    {t(
                                      "trips.myTrips.localExperiences.tourDetails.group"
                                    )}
                                    ,{" "}
                                  </span>
                                  <span>
                                    {g.group_breakdown.quantity}{" "}
                                    {t(
                                      "trips.myTrips.localExperiences.tourDetails.person"
                                    )}
                                  </span>{" "}
                                  :{" "}
                                  <span>
                                    {
                                      g.group_breakdown.price
                                        .customer_base_price
                                    }
                                  </span>
                                  <span>
                                    {g.group_breakdown.price.currency}
                                  </span>
                                </div>
                              )
                            )}
                        </div>
                      </div>
                      <Button
                        className={classes.addToCartButton}
                        disabled={loading}
                        onClick={addToCart}
                        text={loading ? "..." : t(
                          "trips.myTrips.localExperiences.tourDetails.bookNow"
                        )}
                      />
                    </div>
                    <div>{renderParameters}</div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full">
          <div className="p-4 cursor-not-allowed bg-gray-200 border-2 border-solid rounded-[0.875rem]">
            <div className="flex gap-4 items-start">
              <div className="mt-[0.125rem]">
                <SvgIcons.RadioButtonUnChecked />
              </div>
              <div>
                <div className="font-bold text-lg text-gray-500">{title}</div>
                <div className={classes.warning}>
                  {t(
                    "trips.myTrips.localExperiences.tourDetails.participantsForThisActivity"
                  )}{" "}
                  {t("trips.myTrips.localExperiences.tourDetails.min")}:{" "}
                  {participantsRange?.min} -{" "}
                  {t("trips.myTrips.localExperiences.tourDetails.max")}:
                  {participantsRange?.max}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GygTourOption;
